import type { CostTierId, ModelView, PriceMode } from "../shared/contracts.ts";
import { resolveWholeRouteBest, wholeRouteTaskCost } from "../shared/pricing-resolution.ts";
export { resolveWholeRouteBest } from "../shared/pricing-resolution.ts";

export type { PriceMode } from "../shared/contracts.ts";
export type ScoreMetric = "intelligenceIndex" | "codingIndex" | "agenticIndex";
export type PriceBandId = "cheap" | Exclude<CostTierId, "unknown">;
export type PricingActivityId = "agentic-coding" | "research" | "story-without-tools";

export interface PricingActivity {
  id: PricingActivityId;
  label: string;
  sessionLabel: string;
  description: string;
  tokenCount: number;
  cacheShare: number;
  inputShare: number;
  outputShare: number;
  tierBoundaries?: readonly [number, number, number, number, number];
  positionPowers?: Partial<Record<PriceBandId, number>>;
}

export const PRICING_ACTIVITIES: ReadonlyArray<PricingActivity> = [
  {
    id: "story-without-tools",
    label: "Story",
    sessionLabel: "100K token session",
    description: "A long story—roughly 225–270 book pages. 10% input, 90% output, no cache (favorable for older models).",
    tokenCount: 100_000,
    cacheShare: 0,
    inputShare: 0.10,
    outputShare: 0.90,
    tierBoundaries: [0.05, 0.25, 0.75, 1.75, 4.5],
  },
  {
    id: "research",
    label: "Research Question",
    sessionLabel: "1M token session",
    description: "A deep technical question or a heavily sourced research question. 70% cache, 25% input, 5% output.",
    tokenCount: 1_000_000,
    cacheShare: 0.70,
    inputShare: 0.25,
    outputShare: 0.05,
  },
  {
    id: "agentic-coding",
    label: "Agentic coding",
    sessionLabel: "100M token session",
    description: "A ~3-hour autonomous coding run. 95% cache, 4% input, 1% output.",
    tokenCount: 100_000_000,
    cacheShare: 0.95,
    inputShare: 0.04,
    outputShare: 0.01,
    tierBoundaries: [2, 10, 30, 60, 185],
    positionPowers: { premium: 0.5 },
  },
];

const TASK_V2_PRICING_ACTIVITIES: ReadonlyArray<PricingActivity> = (["agentic-coding", "research", "story-without-tools"] as const).map((id) => {
  const activity = PRICING_ACTIVITIES.find((candidate) => candidate.id === id)!;
  const { tierBoundaries: _tierBoundaries, positionPowers: _positionPowers, ...base } = activity;
  return activity.id === "agentic-coding"
    ? { ...base, tierBoundaries: [7.5, 25, 50, 92.5, 185] as const }
    : base;
});

const TASK_V1_PRICING_ACTIVITIES: ReadonlyArray<PricingActivity> = [
  {
    id: "agentic-coding",
    label: "Agentic coding",
    sessionLabel: "100M token session",
    description: "Task price estimates 95% cache reads · 4% input · 1% output. No cache rate means 99% input · 1% output.",
    tokenCount: 100_000_000,
    cacheShare: 0.95,
    inputShare: 0.04,
    outputShare: 0.01,
  },
  {
    id: "research",
    label: "Research",
    sessionLabel: "500K token session",
    description: "Task price estimates 70% cache reads · 25% input · 5% output. No cache rate means 95% input · 5% output.",
    tokenCount: 500_000,
    cacheShare: 0.70,
    inputShare: 0.25,
    outputShare: 0.05,
  },
  {
    id: "story-without-tools",
    label: "Story without tools",
    sessionLabel: "20K token session",
    description: "Task price estimates 10% input · 90% output.",
    tokenCount: 20_000,
    cacheShare: 0,
    inputShare: 0.10,
    outputShare: 0.90,
  },
];

export const DEFAULT_PRICING_ACTIVITY = PRICING_ACTIVITIES.find((activity) => activity.id === "story-without-tools")!;
export const TASK_COST_TIER_VERSION = "cadence-task-cost-v3";

export interface EffectivePricing {
  cacheRead: number | null;
  input: number | null;
  output: number | null;
  value: number | null;
  costTier: CostTierId;
  source: string;
  fallback: boolean;
  cacheFallback: boolean;
  isTaskPrice: boolean;
  routeKind?: "creator-msrp" | "openrouter" | "legacy-flat" | "unavailable";
  provider?: string | null;
  tag?: string | null;
  fingerprint?: string | null;
  observationTime?: string | null;
  endpointUrl?: string | null;
  endpointSha256?: string | null;
  freshness?: "current" | "stale" | "archive" | "legacy" | "unavailable";
  diagnostics?: string[];
}

export interface TierDefinition {
  name: string;
  symbol: string;
  range: string;
  min: number;
  max: number | null;
}

export interface TierScheme {
  order: PriceBandId[];
  tiers: Record<PriceBandId | "unknown", TierDefinition>;
  taskPriced: boolean;
}

const UNKNOWN_TIER = { name: "Unpriced", symbol: "—", range: "not available", min: 0, max: null } as const;

const V3_TIER_SCHEME: TierScheme = {
  order: ["cheap", "utility", "value", "core", "premium", "apex"],
  taskPriced: false,
  tiers: {
    cheap: { name: "Cheap", symbol: "$", range: "under $0.075", min: 0, max: 0.075 },
    utility: { name: "Utility", symbol: "$$", range: "$0.075–0.25", min: 0.075, max: 0.25 },
    value: { name: "Value", symbol: "$$$", range: "$0.25–1.00", min: 0.25, max: 1 },
    core: { name: "Core", symbol: "$$$$", range: "$1.00–2.25", min: 1, max: 2.25 },
    premium: { name: "Premium", symbol: "$$$$$", range: "$2.25–4.50", min: 2.25, max: 4.5 },
    apex: { name: "Apex", symbol: "$$$$$$", range: "$4.50+", min: 4.5, max: null },
    unknown: UNKNOWN_TIER,
  },
};

const V2_TIER_SCHEME: TierScheme = {
  order: ["cheap", "utility", "value", "core", "premium", "apex"],
  taskPriced: false,
  tiers: {
    cheap: { name: "Cheap", symbol: "$", range: "under $0.25", min: 0, max: 0.25 },
    utility: { name: "Utility", symbol: "$$", range: "$0.25–0.75", min: 0.25, max: 0.75 },
    value: { name: "Value", symbol: "$$$", range: "$0.75–2.50", min: 0.75, max: 2.5 },
    core: { name: "Core", symbol: "$$$$", range: "$2.50–7.50", min: 2.5, max: 7.5 },
    premium: { name: "Premium", symbol: "$$$$$", range: "$7.50–15", min: 7.5, max: 15 },
    apex: { name: "Apex", symbol: "$$$$$$", range: "$15+", min: 15, max: null },
    unknown: UNKNOWN_TIER,
  },
};

const LEGACY_TIER_SCHEME: TierScheme = {
  order: ["utility", "value", "core", "premium", "apex"],
  taskPriced: false,
  tiers: {
    cheap: { name: "Cheap", symbol: "$", range: "under $0.25", min: 0, max: 0.25 },
    utility: { name: "Utility", symbol: "$", range: "under $0.75", min: 0, max: 0.75 },
    value: { ...V2_TIER_SCHEME.tiers.value, symbol: "$$" },
    core: { ...V2_TIER_SCHEME.tiers.core, symbol: "$$$" },
    premium: { ...V2_TIER_SCHEME.tiers.premium, symbol: "$$$$" },
    apex: { ...V2_TIER_SCHEME.tiers.apex, symbol: "$$$$$" },
    unknown: UNKNOWN_TIER,
  },
};

function stablePrice(value: number): number {
  return Math.round(value * 1_000_000_000) / 1_000_000_000;
}

function fixedPrice(value: number, decimals: number): string {
  const scale = 10 ** decimals;
  const rounded = Math.round((value + Number.EPSILON) * scale) / scale;
  return rounded.toFixed(decimals);
}

export function formatPrice(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return "—";
  const stable = stablePrice(value);
  if (stable === 0) return "$0.00";
  if (Number.isInteger(stable)) return `$${stable.toFixed(0)}`;
  if (Math.abs(stable) < 0.01) return `$${fixedPrice(stable, 4)}`;
  return `$${fixedPrice(stable, 2)}`;
}

function compactBandPrice(value: number): string {
  const stable = stablePrice(value);
  if (stable === 0) return "$0.00";
  if (Number.isInteger(stable)) return `$${stable.toFixed(0)}`;
  for (let decimals = 2; decimals <= 4; decimals += 1) {
    const scale = 10 ** decimals;
    if (Math.abs(Math.round(stable * scale) / scale - stable) < 1e-9) return `$${stable.toFixed(decimals)}`;
  }
  return `$${stable.toFixed(4)}`;
}

function taskTierScheme(activity: PricingActivity): TierScheme {
  const scale = activity.tokenCount / 1_000_000;
  const boundaries = activity.tierBoundaries ?? [0.075, 0.25, 1, 2.25, 4.5].map((boundary) => boundary * scale);
  const pricedTiers = Object.fromEntries(V3_TIER_SCHEME.order.map((id, index) => {
    const tier = V3_TIER_SCHEME.tiers[id];
    const min = index === 0 ? 0 : boundaries[index - 1]!;
    const max = index === V3_TIER_SCHEME.order.length - 1 ? null : boundaries[index]!;
    const range = id === "cheap"
      ? `under ${compactBandPrice(max!)}`
      : id === "apex"
        ? `${compactBandPrice(min)}+`
        : `${compactBandPrice(min)}–${compactBandPrice(max!)}`;
    return [id, { ...tier, min, max, range }];
  }));
  const tiers = { ...pricedTiers, unknown: UNKNOWN_TIER } as TierScheme["tiers"];
  return { order: [...V3_TIER_SCHEME.order], tiers, taskPriced: true };
}

export function pricingActivitiesForVersion(version: string): ReadonlyArray<PricingActivity> {
  if (version === "cadence-task-cost-v1") return TASK_V1_PRICING_ACTIVITIES;
  if (version === "cadence-task-cost-v2") return TASK_V2_PRICING_ACTIVITIES;
  return PRICING_ACTIVITIES;
}

export function pricingActivity(id: PricingActivityId, version = TASK_COST_TIER_VERSION): PricingActivity {
  const activities = pricingActivitiesForVersion(version);
  return activities.find((activity) => activity.id === id) ?? activities[0] ?? DEFAULT_PRICING_ACTIVITY;
}

export function taskPrice(
  cacheRead: number | null,
  input: number | null,
  output: number | null,
  activity: PricingActivity,
): number | null {
  if (input === null || output === null) return null;
  return wholeRouteTaskCost({ cacheReadPricePerMillion: cacheRead, inputPricePerMillion: input, outputPricePerMillion: output }, activity);
}

export function activityMix(activity: PricingActivity, cacheAvailable = true): { cache: number; input: number; output: number } {
  return cacheAvailable
    ? { cache: activity.cacheShare, input: activity.inputShare, output: activity.outputShare }
    : { cache: 0, input: activity.inputShare + activity.cacheShare, output: activity.outputShare };
}

export function isTaskPricingVersion(version: string): boolean {
  return version.startsWith("cadence-task-cost-");
}

export function tierSchemeForVersion(version: string, activity?: PricingActivity): TierScheme {
  if (version === "cadence-cost-v1") return LEGACY_TIER_SCHEME;
  if (version === "cadence-cost-v2") return V2_TIER_SCHEME;
  if (version === "cadence-cost-v3") return V3_TIER_SCHEME;
  return taskTierScheme(activity ?? pricingActivity(DEFAULT_PRICING_ACTIVITY.id, version));
}

export function priceBandFor(pricing: Pick<EffectivePricing, "value" | "costTier">, tierScheme: TierScheme): PriceBandId | "unknown" {
  if (pricing.value === null) return "unknown";
  if (!tierScheme.taskPriced) {
    if (pricing.costTier === "unknown") return "unknown";
    const cheapCeiling = tierScheme.tiers.cheap.max;
    if (tierScheme.order.includes("cheap") && pricing.costTier === "utility" && cheapCeiling !== null && pricing.value < cheapCeiling) return "cheap";
    return pricing.costTier;
  }
  return tierScheme.order.find((id) => {
    const tier = tierScheme.tiers[id];
    return pricing.value! >= tier.min && (tier.max === null || pricing.value! < tier.max);
  }) ?? "unknown";
}

function finitePrice(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

export function effectivePricing(
  model: ModelView,
  mode: PriceMode,
  activity: PricingActivity | undefined = undefined,
  costTierVersion = TASK_COST_TIER_VERSION,
): EffectivePricing {
  const selectedActivity = activity ?? pricingActivity(DEFAULT_PRICING_ACTIVITY.id, costTierVersion);
  const taskPricing = isTaskPricingVersion(costTierVersion);

  if (mode === "best" && taskPricing) return resolveWholeRouteBest(model, selectedActivity);

  if (mode === "original") {
    const cacheRead = finitePrice(model.originalCacheReadPricePerMillion);
    const input = finitePrice(model.originalInputPricePerMillion);
    const output = finitePrice(model.originalOutputPricePerMillion);
    const status = model.originalPricingStatus ?? "unresearched";
    const available = status === "priced" && input !== null && output !== null;
    const source = available
      ? "Original launch MSRP"
      : status === "not-offered"
        ? "Original unavailable · creator API not offered at launch"
        : status === "unresolved"
          ? "Original unavailable · launch evidence unresolved"
          : "Original unavailable · not yet researched";
    return {
      cacheRead: available ? cacheRead : null,
      input: available ? input : null,
      output: available ? output : null,
      value: available
        ? taskPricing
          ? taskPrice(cacheRead, input, output, selectedActivity)
          : finitePrice(model.originalBlendedPricePerMillion)
        : null,
      costTier: available ? model.originalCostTier ?? "unknown" : "unknown",
      source,
      fallback: false,
      cacheFallback: available && taskPricing && selectedActivity.cacheShare > 0 && cacheRead === null,
      isTaskPrice: taskPricing,
    };
  }

  const msrpCacheRead = finitePrice(model.msrpCacheReadPricePerMillion);
  const msrpInput = finitePrice(model.msrpInputPricePerMillion);
  const msrpOutput = finitePrice(model.msrpOutputPricePerMillion);
  const msrpBlended = finitePrice(model.msrpBlendedPricePerMillion);
  const hasValidMsrpCache = model.msrpCacheReadPricePerMillion === null || msrpCacheRead !== null;
  const hasMsrp = model.msrpAudited !== false && hasValidMsrpCache && (taskPricing
    ? msrpInput !== null && msrpOutput !== null
    : msrpBlended !== null);
  const useMsrp = mode === "msrp" && hasMsrp;
  if (mode === "msrp" && taskPricing && !useMsrp && model.currentPricingRoutes) {
    const best = resolveWholeRouteBest(model, selectedActivity);
    return { ...best, fallback: best.value !== null, source: best.value === null
      ? "Creator MSRP unavailable; no current complete fallback route"
      : `${best.source} · creator MSRP unavailable` };
  }
  const cacheRead = useMsrp ? msrpCacheRead : finitePrice(model.cacheReadPricePerMillion);
  const input = useMsrp ? msrpInput : finitePrice(model.inputPricePerMillion);
  const output = useMsrp ? msrpOutput : finitePrice(model.outputPricePerMillion);
  const storedBlended = useMsrp ? msrpBlended : finitePrice(model.blendedPricePerMillion);
  return {
    cacheRead,
    input,
    output,
    value: taskPricing ? taskPrice(cacheRead, input, output, selectedActivity) : storedBlended,
    costTier: useMsrp ? model.msrpCostTier : model.costTier,
    source: useMsrp
      ? (model.msrpSource ?? "").includes("price") ? model.msrpSource! : `${model.msrpSource ?? model.companyName} creator price`
      : mode === "msrp"
        ? "OpenRouter best · creator price unavailable"
        : model.msrpSource === "Official historical price"
          ? "Official historical price · no current OpenRouter route"
          : "Lowest current complete route",
    fallback: mode === "msrp" && !useMsrp,
    cacheFallback: taskPricing && selectedActivity.cacheShare > 0 && cacheRead === null && input !== null && output !== null,
    isTaskPrice: taskPricing,
  };
}
